import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, ShieldCheck, ShieldOff, Mail, Loader2, CheckCircle2, Monitor, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

export default function SecuritySettingsPage() {
  const { user } = useAuth();
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmCode, setConfirmCode] = useState('');
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  useEffect(() => {
    loadStatus();
    loadTrustedDevices();
  }, []);

  const loadStatus = async () => {
    try {
      const res = await authAPI.get2FAStatus();
      setTwoFAEnabled(res.data.two_fa_enabled);
    } catch {
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  const loadTrustedDevices = async () => {
    setDevicesLoading(true);
    try {
      const res = await authAPI.getTrustedDevices();
      setTrustedDevices(res.data);
    } catch { /* silent fail */ }
    setDevicesLoading(false);
  };

  const handleRevokeDevice = async (deviceId) => {
    try {
      await authAPI.revokeTrustedDevice(deviceId);
      setTrustedDevices(prev => prev.filter(d => d.id !== deviceId));
      toast.success('Device removed');
    } catch {
      toast.error('Failed to remove device');
    }
  };

  const handleRevokeAll = async () => {
    try {
      await authAPI.revokeAllTrustedDevices();
      setTrustedDevices([]);
      localStorage.removeItem('device_token');
      toast.success('All trusted devices removed');
    } catch {
      toast.error('Failed to remove devices');
    }
  };

  const handleEnable = async () => {
    setActionLoading(true);
    try {
      await authAPI.enable2FA();
      setShowConfirm(true);
      toast.success('Verification code sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmEnable = async (e) => {
    e.preventDefault();
    if (confirmCode.length !== 6) {
      toast.error('Please enter the full 6-digit code');
      return;
    }
    setActionLoading(true);
    try {
      const res = await authAPI.confirmEnable2FA({ code: confirmCode });
      setTwoFAEnabled(res.data.two_fa_enabled);
      setShowConfirm(false);
      setConfirmCode('');
      toast.success('Two-factor authentication enabled!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable = async () => {
    setActionLoading(true);
    try {
      const res = await authAPI.disable2FA();
      setTwoFAEnabled(res.data.two_fa_enabled);
      toast.success('Two-factor authentication disabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center pt-16 md:pt-0">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <main className="flex-1 overflow-auto pt-16 md:pt-0">
        <div className="max-w-2xl mx-auto px-6 py-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground tracking-tight" data-testid="security-page-title">Security Settings</h1>
              <p className="text-sm text-muted-foreground">Manage your account security preferences</p>
            </div>
          </div>

          {/* 2FA Card */}
          <div className="bg-card border border-border rounded-xl p-6" data-testid="2fa-settings-card">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {twoFAEnabled ? (
                  <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5 text-emerald-600" />
                  </div>
                ) : (
                  <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center">
                    <ShieldOff className="h-5 w-5 text-amber-600" />
                  </div>
                )}
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Two-Factor Authentication</h2>
                  <p className="text-sm text-muted-foreground">Add an extra layer of security to your account</p>
                </div>
              </div>
              <span
                className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                  twoFAEnabled
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    : 'bg-amber-50 text-amber-700 border border-amber-200'
                }`}
                data-testid="2fa-status-badge"
              >
                {twoFAEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 mb-5">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Email-based verification</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    When enabled, a 6-digit code will be sent to <span className="font-medium">{user?.email}</span> each time you log in. 
                    The code expires after 5 minutes.
                  </p>
                </div>
              </div>
            </div>

            {showConfirm ? (
              <form onSubmit={handleConfirmEnable} className="space-y-4" data-testid="2fa-confirm-form">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    A verification code has been sent to your email. Enter it below to activate 2FA.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-code">Verification Code</Label>
                  <Input
                    id="confirm-code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="Enter 6-digit code"
                    value={confirmCode}
                    onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="bg-card max-w-xs text-lg tracking-widest"
                    data-testid="2fa-confirm-code-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    type="submit"
                    disabled={actionLoading || confirmCode.length !== 6}
                    className="bg-primary text-white"
                    data-testid="2fa-confirm-enable-btn"
                  >
                    {actionLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                    Confirm & Enable
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setShowConfirm(false); setConfirmCode(''); }}
                    data-testid="2fa-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : twoFAEnabled ? (
              <Button
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={handleDisable}
                disabled={actionLoading}
                data-testid="2fa-disable-btn"
              >
                {actionLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldOff className="mr-2 h-4 w-4" />}
                Disable Two-Factor Authentication
              </Button>
            ) : (
              <Button
                className="bg-primary text-white"
                onClick={handleEnable}
                disabled={actionLoading}
                data-testid="2fa-enable-btn"
              >
                {actionLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                Enable Two-Factor Authentication
              </Button>
            )}
          </div>

          {/* Trusted Devices Card */}
          {twoFAEnabled && (
            <div className="bg-card border border-border rounded-xl p-6 mt-6" data-testid="trusted-devices-card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center">
                    <Monitor className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Trusted Devices</h2>
                    <p className="text-sm text-muted-foreground">Devices that skip 2FA verification for 30 days</p>
                  </div>
                </div>
                {trustedDevices.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 text-xs"
                    onClick={handleRevokeAll}
                    data-testid="revoke-all-devices-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                    Remove All
                  </Button>
                )}
              </div>

              {devicesLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : trustedDevices.length === 0 ? (
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <p className="font-body text-sm text-muted-foreground">
                    No trusted devices. Check "Remember this device" during 2FA verification to add one.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {trustedDevices.map((device) => (
                    <div
                      key={device.id}
                      className="flex items-center justify-between py-3 px-4 bg-muted/30 rounded-lg border border-border/50"
                      data-testid={`trusted-device-${device.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Monitor className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="font-body text-sm font-medium text-foreground">
                            Device {device.id}
                          </p>
                          <p className="font-body text-xs text-muted-foreground">
                            Trusted since {new Date(device.created_at).toLocaleDateString()} · Expires {new Date(device.expires_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-red-500 h-8 w-8 p-0"
                        onClick={() => handleRevokeDevice(device.id)}
                        data-testid={`revoke-device-${device.id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Account Info */}
          <div className="bg-card border border-border rounded-xl p-6 mt-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Account Information</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-border/50">
                <span className="text-sm text-muted-foreground">Name</span>
                <span className="text-sm font-medium" data-testid="account-name">{user?.name}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-border/50">
                <span className="text-sm text-muted-foreground">Email</span>
                <span className="text-sm font-medium" data-testid="account-email">{user?.email}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-muted-foreground">Role</span>
                <span className="text-sm font-medium capitalize" data-testid="account-role">{user?.role?.replace('_', ' ')}</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
